import React from 'react';
import challengeMobile from '../../../assets/images/rabbitX/challenge_mobile.webp';
import Constants from '../../../app/utils/constants';
import colors from '../../theme/color';
import { motion } from 'framer-motion';

const ChallengeSection = () => {
  const challengePoints = Constants.rabbitxChallengePoints;

  return (
    <section className="relative z-20 w-full overflow-hidden pb-24 md:pb-28 lg:pb-32 2xl:pb-40 min-[2560px]:pb-56">
      <div
        className="pointer-events-none absolute -left-36 top-[16%] hidden h-[520px] w-[420px] rounded-full opacity-[0.08] blur-3xl md:block"
        style={{ backgroundColor: colors.rabbitXAccent }}
      />
      <div
        className="pointer-events-none absolute -right-24 top-[42%] hidden h-[480px] w-[360px] rounded-full opacity-[0.14] blur-3xl lg:block"
        style={{ backgroundColor: colors.rabbitXGlowPurple }}
      />

      <div className="mx-auto w-full max-w-[1240px] px-5 md:px-8 lg:px-12 2xl:max-w-[1800px] 2xl:px-16 min-[2560px]:max-w-[2400px] min-[2560px]:px-24">
        <div className="mx-auto mb-12 flex w-full max-w-[1120px] items-center justify-center gap-4 md:mb-16 md:gap-6 2xl:mb-20 2xl:max-w-[1400px] 2xl:gap-8 min-[2560px]:mb-28 min-[2560px]:max-w-[2000px] min-[2560px]:gap-12">
          <span
            className="relative block h-px w-[30px] md:w-[68px] sm:w-[86px]"
            style={{ backgroundColor: `${colors.rabbitXAccent}CC` }}
          >
            <span
              className="absolute -left-[4px] top-1/2 h-[5px] w-[5px] -translate-y-1/2 rounded-full"
              style={{ backgroundColor: `${colors.rabbitXAccent}F2` }}
            />
          </span>
          <span className="font-rajdhani text-[2rem] font-semibold leading-none text-white md:text-[3rem] lg:text-[3.3rem] 2xl:text-[4.2rem] min-[2560px]:text-[6rem]">The Challenge</span>
          <span
            className="relative block h-px w-[30px] md:w-[68px] sm:w-[86px]"
            style={{ backgroundColor: `${colors.rabbitXAccent}CC` }}
          >
            <span
              className="absolute -right-[4px] top-1/2 h-[5px] w-[5px] -translate-y-1/2 rounded-full"
              style={{ backgroundColor: `${colors.rabbitXAccent}F2` }}
            />
          </span>
        </div>

        <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-8 xl:gap-10 2xl:gap-12 min-[2560px]:gap-14">
          <motion.div
            className="flex min-w-0 flex-col lg:min-h-[500px] 2xl:min-h-[580px] min-[2560px]:min-h-[720px]"
            initial={{ x: -90, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: false, amount: 0.45 }}
            transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1], delay: 0.12 }}
          >
            <p className="w-full text-center lg:text-start max-w-[1000px] font-gilroy-semibold text-[1.28rem] leading-[1.45] tracking-[0.015em] text-white md:text-[1.46rem] 2xl:max-w-[1200px] 2xl:text-[1.85rem] min-[2560px]:max-w-[1600px] min-[2560px]:text-[2.6rem]">
              Crypto trading platforms often suffer from:
            </p>

            <p className="mt-5 w-full text-center lg:text-start max-w-[760px] font-gilroy-semibold text-[1.12rem] leading-[1.45] text-white md:text-[1.2rem] 2xl:max-w-[1000px] 2xl:text-[1.55rem] min-[2560px]:max-w-[1400px] min-[2560px]:text-[2.2rem]">
              RabbitX needed to simplify advanced trading while maintaining powerful functionality.
            </p>

            <div className="mt-7 grid w-full max-w-[640px] grid-cols-2 gap-5 sm:grid-cols-2 2xl:mt-10 2xl:max-w-[900px] 2xl:gap-8 min-[2560px]:mt-14 min-[2560px]:max-w-[1200px] min-[2560px]:gap-12">
              {challengePoints.map((point, index) => (
                <motion.article
                  key={point.serial}
                  className={`relative isolate overflow-hidden min-h-[108px] rounded-[12px] px-5 py-3.5 2xl:min-h-[140px] 2xl:rounded-[18px] 2xl:px-8 2xl:py-8 min-[2560px]:min-h-[190px] min-[2560px]:rounded-[24px] min-[2560px]:px-12 min-[2560px]:py-12 ${index === challengePoints.length - 1 ? 'sm:col-span-2 sm:mx-auto sm:w-full sm:max-w-[300px] 2xl:max-w-[420px] min-[2560px]:max-w-[560px]' : ''}`}
                  initial={{
                    opacity: 0,
                    x: [-54, 52, -48, 46, 0][index] ?? 0,
                    y: [24, 20, 28, 22, 32][index] ?? 24,
                    rotate: [-7, 6, -5, 5, -4][index] ?? 0,
                  }}
                  whileInView={{ opacity: 1, x: 0, y: 0, rotate: 0 }}
                  viewport={{ once: false, amount: 0.35 }}
                  transition={{
                    duration: 1.05,
                    delay: 0.22 + index * 0.14,
                    type: 'spring',
                    bounce: 0.2,
                  }}
                  style={{
                    backgroundColor: 'rgba(8, 3, 18, 0.94)',
                    backgroundImage: `linear-gradient(132deg, ${colors.rabbitXChallengeCardA}12 0%, ${colors.rabbitXChallengeCardB}10 46%, ${colors.rabbitXChallengeCardC}52 100%)`,
                  }}
                >
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 -z-10"
                    style={{
                      backgroundImage:
                        'linear-gradient(90deg, rgba(308, 52, 76, 0.15) 0%, rgba(238, 52, 76, 0) 100%)',
                    }}
                  />
                  <span
                    className="relative z-10 block font-gilory-bold text-[2.6rem] font-semibold leading-none 2xl:text-[3.4rem] min-[2560px]:text-[4.8rem]"
                    style={{ color: colors.rabbitXAccent }}
                  >
                    {point.serial}
                  </span>
                  <p
                    className="relative z-10 mt-1.5 max-w-[248px] font-gilroy-medium text-[0.84rem] leading-[1.32] sm:text-[0.88rem] 2xl:max-w-[320px] 2xl:text-[1.1rem] min-[2560px]:max-w-[440px] min-[2560px]:text-[1.5rem]"
                    style={{ color: `${colors.rabbitXTextLight}FF` }}
                  >
                    {point.text}
                  </p>
                </motion.article>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="group relative mx-auto w-full max-w-[700px] shrink-0 rounded-[30px] md:max-w-[780px] lg:mx-0 lg:w-full lg:min-w-[600px] lg:max-w-[980px] 2xl:max-w-[1200px] 2xl:rounded-[40px] min-[2560px]:max-w-[1500px] min-[2560px]:rounded-[52px]"
          >
            <span
              className="pointer-events-none absolute inset-0 z-10 rounded-[30px] opacity-0 transition-opacity duration-700 ease-out group-hover:opacity-100 2xl:rounded-[40px] min-[2560px]:rounded-[52px]"
              style={{
                boxShadow: `0 0 0 1px ${colors.rabbitXAccent}73 inset, 0 0 48px ${colors.rabbitXGlowPurple}52, 0 0 84px ${colors.rabbitXAccent}3D`,
              }}
            />

            <motion.div
              className="relative h-full w-full overflow-hidden rounded-[30px] px-10 pt-0 pb-6 md:px-12 md:pt-0 md:pb-8 2xl:rounded-[40px] 2xl:px-20 2xl:pt-0 2xl:pb-10 min-[2560px]:rounded-[52px] min-[2560px]:px-28 min-[2560px]:pt-0 min-[2560px]:pb-14"
              style={{
                backgroundImage: `linear-gradient(180deg, ${colors.rabbitXAccent}00 0%, ${colors.rabbitXAccent}33 100%)`,
              }}
              whileHover="hover"
              initial="rest"
              animate="rest"
            >
              {/* Wipe overlay from bottom to up */}
              <motion.div
                className="pointer-events-none absolute inset-0 z-0"
                variants={{
                  rest: { y: '100%', opacity: 0 },
                  hover: { y: '0%', opacity: 1 },
                }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                style={{
                  backgroundImage: `linear-gradient(180deg, ${colors.rabbitXAccent}1A 0%, ${colors.rabbitXAccent}66 100%)`,
                }}
              />
              <motion.img
              src={challengeMobile}
              alt="RabbitX challenge mobile interface"
              className="relative z-10 mx-auto block w-full max-w-[270px] -mt-6 sm:-mt-10 md:-mt-14 lg:-mt-16 2xl:-mt-20 min-[2560px]:-mt-24 object-contain md:max-w-[250px] lg:w-auto lg:max-w-[300px] 2xl:max-w-[420px] min-[2560px]:max-w-[560px]"
              initial={{ y: -150, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: false, amount: 0.45 }}
              transition={{ duration: 2.8, ease: [0.16, 1, 0.3, 1] }}
            />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ChallengeSection;
